import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import api from "../services/api";
import { refreshSession, signInService } from "../services/sessions";
import { SignInCredentials } from "../types/session";
import { User, UserPermissions } from "../types/user";

type AuthProviderProps = {
  children: React.ReactNode;
};

export type AuthState = {
  token: string;
  user: User;
  permissions: UserPermissions;
};

export type AuthContextData = {
  token: string;
  user: User;
  loading: boolean;
  signIn(credentials: SignInCredentials): Promise<void>;
  persistUser(
    user: User,
    permissions: UserPermissions,
    token: string
  ): Promise<void>;
  signOut(): void;
};

export const AuthContext = createContext<AuthContextData>(
  {} as AuthContextData
);

export function AuthProvider({ children }: AuthProviderProps) {
  const [data, setData] = useState<AuthState>({} as AuthState);
  const [loading, setLoading] = useState(true);

  const signOut = useCallback(async () => {
    setData({} as AuthState);
    localStorage.clear();
  }, []);

  useEffect(() => {
    async function loadStorageData(): Promise<void> {
      try {
        const localToken = localStorage.getItem("@datasales:token");
        const localUser = localStorage.getItem("@datasales:user");

        if (localToken && localUser) {
          const parsedUser = JSON.parse(localUser);
          const response = await refreshSession({
            token: localToken,
            userId: parsedUser.id,
          });
          const { token, user, permissions } = response;

          api.defaults.headers.common.authorization = `${token}`;

          setData({ token, user, permissions });
        }
      } catch (err) {
        signOut();
      } finally {
        setLoading(false);
      }
    }

    loadStorageData();
  }, [signOut]);

  const signIn = useCallback(async ({ email, password }: any) => {
    const response = await signInService({ email, password });

    const { token, user, permissions } = response;

    api.defaults.headers.common.authorization = `${token}`;

    localStorage.setItem("@datasales:token", token);
    localStorage.setItem("@datasales:user", JSON.stringify(user));
    localStorage.setItem("@datasales:permissions", JSON.stringify(permissions));
    setData({ token, user, permissions });
  }, []);

  const persistUser = useCallback(
    async (user: User, permissions: UserPermissions, token: string) => {
      localStorage.setItem("@datasales:token", token);
      localStorage.setItem("@datasales:user", JSON.stringify(user));
      localStorage.setItem("@datasales:permissions", JSON.stringify(permissions));
      setData({ token, user, permissions });
    },
    []
  );

  const authContextData = useMemo(() => {
    return {
      token: data.token,
      loading,
      user: data.user,
      signIn,
      signOut,
      persistUser,
    };
  }, [data.token, data.user, signIn, loading, signOut, persistUser]);

  return (
    <AuthContext.Provider value={authContextData}>
      {children}
    </AuthContext.Provider>
  );
}
